// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toggleModal } from '../../../storage/actions/modalsActions';
import TableElement from './TableElement';
import CreateDocumentForm from './CreateDocumentForm';
import {
  addWorkOrderProjectOrCompany,
  editWorkOrderProjectOrCompany,
  deleteWorkOrderProjectOrCompany,
} from '../../../storage/actions/workOrdersActions';
import Notification from '../../notification';

const ProjectAndCompanyControlModal = ({
  modalState,
  toggleModal,
  addWorkOrderProjectOrCompany,
  editWorkOrderProjectOrCompany,
  deleteWorkOrderProjectOrCompany,
}) => {
  const [options, setOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const name = modalState?.type?.name?.replace(/^\w/, (c) => c.toUpperCase());

  useEffect(() => {
    if (modalState?.isOpen) {
      setOptions(modalState?.type?.options || []);
      setEditingId(null);
      setEditName('');
    }
  }, [modalState?.isOpen, modalState?.type?.options]);

  const closeModal = () => {
    toggleModal(false, 'projectAndCompanyControlModal');
  };

  const handlerUpdateOptions = (newOptions) => {
    modalState.type.updateData(newOptions);
    setOptions(newOptions);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const startEdit = (id, label) => {
    setEditingId(id);
    setEditName(label);
  };

  // eslint-disable-next-line consistent-return
  const handlerCreate = async (event) => {
    event.preventDefault();
    const { value: documentName } = event.target.documentName;
    if (!documentName.trim()) {
      return Notification('warning', {
        message: 'Please enter a name before saving.',
      });
    }
    const resData = await addWorkOrderProjectOrCompany(modalState.type.linkName, documentName);
    if (resData?.success) {
      const newOptions = [
        ...options,
        {
          value: resData?.payload?.newDocument?._id,
          label: resData?.payload?.newDocument?.name,
        },
      ];
      handlerUpdateOptions(newOptions);
      event.target.reset();
    }
  };

  const handlerDelete = async (id) => {
    if (editingId === id) {
      cancelEdit();
    }
    await deleteWorkOrderProjectOrCompany(modalState.type.linkName, id);
    const newOptions = options.filter((item) => item.value !== id);
    handlerUpdateOptions(newOptions);
  };

  // eslint-disable-next-line consistent-return
  const handlerEdit = async (id, newName) => {
    if (!newName.trim() || !id) {
      return Notification('warning', {
        message: 'Please enter name before saving changes.',
      });
    }
    await editWorkOrderProjectOrCompany(modalState.type.linkName, id, newName);
    const newOptions = options.map((item) => (
      item.value !== id
        ? item
        : { ...item, label: newName }
    ));
    handlerUpdateOptions(newOptions);
    cancelEdit();
  };

  const handlerSaveEdit = (event) => {
    event.preventDefault();
    handlerEdit(editingId, editName);
  };

  if (!modalState?.isOpen) {
    return null;
  }

  return (
    <div
      className="dashboard-modal dashboard-modal--open dashboard-modal--project-company"
      role="presentation"
      onClick={closeModal}
    >
      <div
        className="dashboard-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-company-control-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-modal-header">
          <div className="dashboard-modal-title" id="project-company-control-title">
            {name}
          </div>
          <button
            type="button"
            className="dashboard-modal-close"
            aria-label="close"
            onClick={closeModal}
          />
        </div>
        <div className="dashboard-modal-body wurqly-modal-scroll">
          <CreateDocumentForm
            name={name}
            handlerCreate={handlerCreate}
          />
          <div className="subpage-table-scroll">
            <table className="subpage-table subpage-table--project-company">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="actions-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {
                  options.map(({ label, value }) => (
                    <TableElement
                      key={value}
                      label={label}
                      value={value}
                      isEditing={editingId === value}
                      editName={editingId === value ? editName : ''}
                      onEditStart={startEdit}
                      onEditNameChange={setEditName}
                      onDelete={handlerDelete}
                    />
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
        {editingId ? (
          <div className="dashboard-modal-footer">
            <button
              type="button"
              className="dashboard-btn dashboard-btn--primary"
              aria-label="Save"
              onClick={handlerSaveEdit}
            >
              Save
            </button>
            <button
              type="button"
              className="dashboard-btn dashboard-btn--secondary"
              aria-label="Close"
              onClick={cancelEdit}
            >
              Close
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
  addWorkOrderProjectOrCompany,
  editWorkOrderProjectOrCompany,
  deleteWorkOrderProjectOrCompany,
};

const mapStateToProps = (state) => ({
  modalState: state.modalState.projectAndCompanyControlModal,
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectAndCompanyControlModal);
